import React, { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Folder, FolderOpen, FileText, Image, Table, ChevronUp, ChevronDown, Pencil } from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { stripMarkupAndMarkdown, isUrl } from "@/lib/textUtils";
import { useI18n } from "@/lib/i18n";
import { folderBus } from "@/events/folderBus";
import ItemContextMenu from "@/components/shared/ItemContextMenu";
import type { FolderItem, FolderDataLink, DataItem } from "@/types";
import type { DragPayload } from "@/components/shared/DndProvider";

export interface FolderTreeNodeProps {
  folder: FolderItem;
  folders: FolderItem[];
  folderLinks: FolderDataLink[];
  getDataById: (id: string) => DataItem | undefined;
  depth?: number;
  selectedIds: Set<string>;
  onSelect: (id: string, e?: React.MouseEvent) => void;
  onRenameFolder: (id: string, newName: string) => void;
  onRenameDataItem?: (id: string, newLabel: string) => void;
  onMoveFolder?: (id: string, direction: 'up' | 'down') => void;
  onNewFolder?: (parentId: string | null) => void;
  isFirst?: boolean;
  isLast?: boolean;
  onItemClick?: (linkId: string, dataId: string, e?: React.MouseEvent) => void;
  onRightClickSelect?: (area: string, itemId: string, dataId?: string) => void;
  onDeleteRequest?: () => void;
  globalEntries?: { area: string; itemId: string; dataId?: string }[];
}

const FolderTreeNode = ({
  folder,
  folders,
  folderLinks,
  getDataById,
  depth = 0,
  selectedIds,
  onSelect,
  onRenameFolder,
  onRenameDataItem,
  onMoveFolder,
  onNewFolder,
  isFirst = false,
  isLast = false,
  onItemClick,
  onRightClickSelect,
  onDeleteRequest,
  globalEntries,
}: FolderTreeNodeProps) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const unsub = folderBus.onFolderOpen((id) => {
      if (id === folder.id) {
        setOpen(true);
      }
    });
    return () => { unsub(); };
  }, [folder.id]);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(folder.name);

  const dragPayload: DragPayload = {
    sourceArea: "folder",
    itemIds: [folder.id],
    dataIds: [],
  };

  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: `folder-drag-${folder.id}`,
    data: { ...dragPayload, folderIds: [folder.id] },
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-drop-${folder.id}`,
    data: { area: "folder", folderId: folder.id },
  });

  const childFolders = folders
    .filter((f) => f.parentId === folder.id)
    .sort((a, b) => a.order - b.order);

  const links = folderLinks
    .filter((l) => l.folderId === folder.id)
    .sort((a, b) => a.order - b.order);

  const totalLinksCount = useMemo(() => {
    const countLinks = (fId: string): number => {
      let count = folderLinks.filter(l => l.folderId === fId).length;
      const children = folders.filter(f => f.parentId === fId);
      for (const child of children) {
        count += countLinks(child.id);
      }
      return count;
    };
    return countLinks(folder.id);
  }, [folder.id, folders, folderLinks]);

  const hasChildren = childFolders.length > 0 || links.length > 0;

  const handleDoubleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditValue(folder.name);
    setEditing(true);
  };

  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== folder.name) {
      onRenameFolder(folder.id, trimmed);
    }
    setEditing(false);
  };

  const mergeRefs = (el: HTMLElement | null) => {
    setDropRef(el);
  };

  return (
    <div
      ref={mergeRefs}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      className={`rounded-lg transition-colors ${isOver ? "ring-1 ring-inset ring-primary/50 bg-primary/5" : ""}`}
    >
      <Collapsible open={open} onOpenChange={setOpen}>
        {editing ? (
          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
            style={{ paddingLeft: `${depth * 16 + 8}px` }}
          >
            <span className="shrink-0 w-3" />
            <FolderOpen className="w-3.5 h-3.5 text-primary shrink-0" />
            <input
              autoFocus
              value={editValue}
              data-testid="folder-edit-input"
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setEditing(false);
              }}
              onBlur={commitRename}
              className="flex-1 text-xs px-1 py-0.5 rounded bg-background/60 border border-primary/40 text-foreground focus:outline-none focus:ring-1 focus:ring-ring min-w-0"
            />
          </div>
        ) : (
          <ItemContextMenu
            area="folder-node"
            itemId={folder.id}
            onRename={() => setEditing(true)}
            onNewFolder={onNewFolder}
            onRightClickSelect={(id) => onRightClickSelect?.("folder-node", id)}
            onDelete={() => onDeleteRequest?.()}
            hasItems={links.length > 0}
          >
            <div ref={setDragRef} {...listeners} {...attributes} data-testid="folder-item" className="flex items-center group/folder">
              <CollapsibleTrigger asChild>
                <button
                  className={`min-w-0 flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition-colors hover:bg-primary/10 ${
                    selectedIds.has(folder.id) ? "bg-primary/15 text-primary" : "text-foreground/80"
                  }`}
                  style={{ paddingLeft: `${depth * 16 + 8}px` }}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey) {
                      e.preventDefault();
                    }
                    onSelect(folder.id, e);
                  }}
                   onDoubleClick={handleDoubleClick}
                  onKeyDown={(e) => {
                    if (e.key === 'F2') {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDoubleClick(e);
                    }
                  }}
                >
                  <motion.span
                    animate={{ rotate: open ? 90 : 0 }}
                    transition={{ duration: 0.15 }}
                    className="shrink-0"
                  >
                    <ChevronRight className={`w-3 h-3 ${hasChildren ? "text-muted-foreground" : "text-transparent"}`} />
                  </motion.span>
                  {open ? (
                    <FolderOpen className="w-3.5 h-3.5 text-primary shrink-0" />
                  ) : (
                    <Folder className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                  )}
                  <span data-testid="folder-item-name" className="truncate flex-1 text-left">{folder.name}</span>
                  {totalLinksCount > 0 && (
                    <span className="text-[10px] text-muted-foreground/50 shrink-0">{totalLinksCount}</span>
                  )}
                </button>
              </CollapsibleTrigger>
  
              {/* Folder action buttons */}
              <div className="flex items-center shrink-0 opacity-0 group-hover/folder:opacity-100 transition-opacity mr-1">
                <button
                  onClick={(e) => { e.stopPropagation(); handleDoubleClick(e); }}
                  className="p-1 rounded hover:bg-primary/20 transition-colors"
                  title={t("folder.rename")}
                  data-testid="folder-item-rename"
                >
                  <Pencil className="w-3 h-3 text-muted-foreground hover:text-primary" />
                </button>
                {onMoveFolder && (
                  <div className="flex flex-col ml-0.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveFolder(folder.id, 'up'); }}
                      disabled={isFirst}
                      className="p-0.5 rounded hover:bg-primary/20 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title="위로 이동"
                    >
                      <ChevronUp className="w-3 h-3 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onMoveFolder(folder.id, 'down'); }}
                      disabled={isLast}
                      className="p-0.5 rounded hover:bg-primary/20 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                      title="아래로 이동"
                    >
                      <ChevronDown className="w-3 h-3 text-muted-foreground" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </ItemContextMenu>
        )}

        <CollapsibleContent>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.15 }}
              >
                {childFolders.map((child, i) => (
                  <FolderTreeNode
                    key={child.id}
                    folder={child}
                    folders={folders}
                    folderLinks={folderLinks}
                    getDataById={getDataById}
                    depth={depth + 1}
                    selectedIds={selectedIds}
                    onSelect={onSelect}
                    onRenameFolder={onRenameFolder}
                    onRenameDataItem={onRenameDataItem}
                    onMoveFolder={onMoveFolder}
                    onNewFolder={onNewFolder}
                    isFirst={i === 0}
                    isLast={i === childFolders.length - 1}
                    onItemClick={onItemClick}
                    onRightClickSelect={onRightClickSelect}
                    onDeleteRequest={onDeleteRequest}
                    globalEntries={globalEntries}
                  />
                ))}

                {links.map((link) => {
                  const data = getDataById(link.dataId);
                  if (!data) return null;
                  return (
                    <FolderDataItem
                      key={link.id}
                      linkId={link.id}
                      data={data}
                      depth={depth + 1}
                      isSelected={selectedIds.has(link.id)}
                      onItemClick={onItemClick}
                      onRightClickSelect={onRightClickSelect}
                      onRenameDataItem={onRenameDataItem}
                      onDeleteRequest={onDeleteRequest}
                      globalEntries={globalEntries}
                    />
                  );
                })}
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

/** Individual data item inside a folder */
function FolderDataItem({
  linkId,
  data,
  depth,
  isSelected,
  onItemClick,
  onRightClickSelect,
  onRenameDataItem,
  onDeleteRequest,
  globalEntries,
}: {
  linkId: string;
  data: DataItem;
  depth: number;
  isSelected: boolean;
  onItemClick?: (linkId: string, dataId: string, e?: React.MouseEvent) => void;
  onRightClickSelect?: (area: string, itemId: string, dataId?: string) => void;
  onRenameDataItem?: (id: string, newLabel: string) => void;
  onDeleteRequest?: () => void;
  globalEntries?: { area: string; itemId: string; dataId?: string }[];
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(data.label);

  const Icon = data.type === "image" ? Image : data.type === "table" ? Table : FileText;
  
  const commitRename = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== data.label) {
      onRenameDataItem?.(data.id, trimmed);
    }
    setEditing(false);
  };

  // Build drag payload — include all selected folder items if this item is in selection
  const selectedFolderItems = (globalEntries ?? []).filter(e => e.area === 'folder');
  const isInSelection = selectedFolderItems.some(e => e.itemId === linkId);

  const dragPayload: DragPayload = isInSelection && selectedFolderItems.length > 1
    ? {
        sourceArea: "folder",
        itemIds: selectedFolderItems.map(e => e.itemId),
        dataIds: selectedFolderItems.map(e => e.dataId).filter((id): id is string => !!id),
      }
    : {
        sourceArea: "folder",
        itemIds: [linkId],
        dataIds: [data.id],
      };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `folder-item-drag-${linkId}`,
    data: dragPayload,
  });

  const handleClick = useCallback((e: React.MouseEvent) => {
    onItemClick?.(linkId, data.id, e);
  }, [linkId, data.id, onItemClick]);

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className="min-w-0 w-full" style={{ opacity: isDragging ? 0.4 : 1 }}>
      <ItemContextMenu
        area="folder"
        itemId={linkId}
        dataId={data.id}
        onRename={() => setEditing(true)}
        onRightClickSelect={(id, dId) => onRightClickSelect?.("folder", id, dId)}
        onDelete={() => onDeleteRequest?.()}
      >
        <button
          className={`min-w-0 w-full flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-colors hover:bg-accent/10 ${
            isSelected ? "bg-primary/10 text-primary font-bold" : "text-foreground/70"
          }`}
          data-testid="folder-data-item"
          style={{ paddingLeft: `${depth * 16 + 24}px` }}
          data-selectable-id={linkId}
          data-selectable-data-id={data.id}
          data-selectable-area="folder"
          onClick={handleClick}
        >
          {data.type === "image" ? (
            <img src={data.content} alt={data.label} className="w-4 h-4 rounded object-cover shrink-0" />
          ) : (
            <Icon className="w-3 h-3 text-muted-foreground/60 shrink-0" />
          )}
          
          {editing ? (
            <input
              autoFocus
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRename();
                if (e.key === "Escape") setEditing(false);
              }}
              onBlur={commitRename}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 text-[11px] px-1 py-0.5 rounded bg-background/60 border border-primary/40 text-foreground focus:outline-none focus:ring-1 focus:ring-ring min-w-0"
            />
          ) : (
            <span className="truncate flex-1 text-left max-w-[140px]">
              {data.type === "url" || isUrl(data.content)
                ? data.label
                : (data.type === "text" || data.type === "other"
                  ? stripMarkupAndMarkdown(data.content).slice(0, 25)
                  : data.label)}
            </span>
          )}
        </button>
      </ItemContextMenu>
    </div>
  );
}

export default FolderTreeNode;
